import React, { useState, useEffect } from 'react';
import loadable from '@loadable/component';
import { Helmet } from "react-helmet";
import { sharedConstants, sharedLoggerApi } from 'shared-helpers';
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

// api
import fundingApi from '/src/apis/fundingApi';
import startupApi from '/src/apis/startupApi.js';
import ecosystemApi from '/src/apis/ecosystemApi.js';
import researchApi from '/src/apis/researchApi';

// constants
import { DEF_MAGNITT_DESCRIPTION, CONTACT_FORM_TYPE, HUBSPOT_FORM_SOURCE } from '/src/lib/constants';
import { WEB_URL } from '/src/lib/endpoints';
import { SALES_CONTACT_SUCCESS_MESSAGE } from '/src/lib/messages';
import { CLIENT_LOGOS } from '/src/lib/constants/logoConstants.js';

// helpers
import { formatCount } from '/src/lib/helpers';

// component
import PlanBoxes from './Reusable/PlanBoxes.js';
import Features from './Reusable/Features.js';
import Testimonials from '/src/components/Reusable/Testimonials';
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
const ContactForm  = loadable(() => import(/* webpackChunkName: "Contact" */ '/src/components/ContactForms/ContactForm'));
const SuccessPopup = loadable(() => import(/* webpackChunkName: "SuccessPopup" */ '/src/components/Reusable/SuccessPopup'));

// variables
const LoggerApi     = sharedLoggerApi;
const componentName = 'PricingPage';
const TAXONOMIES    = sharedConstants.TAXONOMIES;



const PricingPage = (props) => {

    const [count, setCount] = useState({ fundingRounds: '', companies: '', research: '', startups: '', ecosystem: '' });
    const { user } = props;

    useEffect(() => {
        getEntityCounts();
    }, []);

    // calculate company count
    useEffect(() => {
        try {
            if (count.companies === '' && count.startups && count.ecosystem) {
                let count1 = parseInt((count.ecosystem.replace(/,/g, '')));
                let count2 = parseInt((count.startups.replace(/,/g, '')));
                let temp_count = count1 + count2;
                temp_count = formatCount(temp_count);
                setCount(prev => ({ 
                    ...prev, 
                    companies: temp_count
                }))  
            }
        } catch (err) { console.log(err) }
    }, [count.startups, count.ecosystem]);

    const getEntityCounts = () => {
        const countTypes = ["fundingRounds", "startups", "ecosystem", "research"];
        countTypes.map((entity, v) => {

            let countAPI = null;
            if (entity === "fundingRounds") {
                countAPI = fundingApi.getFundingBreakdownsCount();
            } else if (entity === "startups") {
                countAPI = startupApi.getStartupsCount();
            } else if (entity === "ecosystem") {
                countAPI = ecosystemApi.getEcosystemsCount();
            } else if (entity === "research") {
                countAPI = researchApi.getResearchCount();
            }

            countAPI.then(response => {
                const value = response.count || response
                setCount(prev => ({
                    ...prev,
                    [entity]: formatCount(value)
                }))
            }).catch(data => {
                // error, sets a default count
                setCount(prev => ({
                    ...prev,
                    [entity]: formatCount(data.totalCount || data.count || data.total || 0)
                }))
                LoggerApi.logError("getEntityCount-" + entity, componentName, data.error)
            })

        })
    }

    return (
        <div className="pricing-page footer-margin-offset">
            <Helmet>
                <meta property="og:site_name" content="MAGNiTT" />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`${WEB_URL}/pricing`} />
                <meta property="og:title" content="Pricing" />
                <title>Pricing | MAGNiTT</title>
                <meta name="HandheldFriendly" content="true" />
                <meta name="MobileOptimized" content="width" />
                <meta name="description" content={DEF_MAGNITT_DESCRIPTION} />
                <meta name="abstract" content={DEF_MAGNITT_DESCRIPTION} />
                <link rel='canonical' href={`${WEB_URL}/pricing`} />
                <script type="application/ld+json">
                    {`
                        {
                            "@context": "http://schema.org",
                            "@type": "BreadcrumbList",
                            "itemListElement": [{
                                "@type": "ListItem",
                                "position": 1,
                                "name": "pricing",
                                "item": "${WEB_URL}/pricing"
                            }]
                        }
                        `}
                </script>
            </Helmet>

            {/* plans section */}
            <div className="section-plans-wrapper">
                <div className="pricing-plans">
                    <div className="page-header">
                        <h2>
                            The Leading Private Capital Data Platform covering Emerging Venture Markets
                        </h2>
                        <p>Verified private capital data (VC, PE & Credit) and research powering strategic decisions across the Middle East, Africa, Southeast Asia, Türkiye and Pakistan</p>
                    </div>
                    <PlanBoxes />
                </div>
                <div className="lines-graphic"></div>
            </div>

            {/* features section */}
            <Features />

            {/* features section - continued */}
            <div className="section-counts">
                <p>Verified Private Market Data. <span className="red">In Real Time.</span></p>
                <p className="subtitle">Comprehensive coverage across venture backed companies, investors, funds, and transactions and exits.</p>
                <div className="row">
                    <div className="col-sm-6 col-md-4 col-lg-3">
                        <div className="feature-icon-box">
                            <i className="fas fa-search-dollar"></i>
                            <span><b>{count.fundingRounds}</b> Verified Funding Rounds</span>
                        </div>
                    </div>
                    <div className="col-sm-6 col-md-4 col-lg-3">
                        <div className="feature-icon-box">
                            <i className="fas fa-briefcase"></i>
                            <span><b>{count.companies}</b> Company & Investor Profiles</span>
                        </div>
                    </div>
                    <div className="col-sm-6 col-md-4 col-lg-3">
                        <div className="feature-icon-box">
                            <i className="fas fa-chart-pie"></i>
                            <span><b>{count.research}</b> In-Depth Industry Reports</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* form */}
            <div className="pricing-contact-form" id="contactForm" data-loggedin={user && user.is_loggedin}>
                <div className="row">
                    <div className="col-md-10 col-lg-7 col-xl-7 mx-auto mb-4 mb-lg-0">
                        <div className="db-box no-background h-100">
                            <div className="text-section">
                                <div className="pricing-clients">
                                    <div className="row">
                                        <p className="pricing-form-header text-center">
											 <span>Trusted by Industry Leaders</span>
                                        </p>
                                        {CLIENT_LOGOS.map((logo, i) => {
                                            return (
                                                <div className="col-6 col-md-4 col-lg-5" key={"logo-" + i}>
                                                    <div className="img-container">
                                                        <img className="img-fluid" src={logo} />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ContactForm
                        taxonomyType={TAXONOMIES.salesContactOptions}
                        formType={CONTACT_FORM_TYPE.enterprise}
                        buttonLabel='Book a discovery call'
                        page={HUBSPOT_FORM_SOURCE.pricing_contact_sales}
                        headerText={
                            <p className="pricing-form-header">
                                <span className="mb-2">Interested in learning more?</span>
                                Use MAGNiTT to make informed investments and strengthen your team's competitive position
                            </p>
                        }
                    />
                </div>
            </div>

            {/* client testimonials */}
            <section className="section-about border-bottom section-testimonials">
                <div className="page-header mt-0">
                    <p className="sub-header">
                        Testimonials
                    </p>
                    <h2>Why Leaders Choose MAGNiTT</h2>
                </div>
                <Testimonials />
            </section>

            {/* modals */}
            <SuccessPopup 
                msg={SALES_CONTACT_SUCCESS_MESSAGE} 
                handleClose={() => { }}
                // specify if the element is used to track conversion of Google Ads
                isGoogleAdsConversionMetric={true}
            />
            <ContactSalesPopup page={HUBSPOT_FORM_SOURCE.sales_pricing_popup} />
            
        </div>
    )
}

const mapStateToProps = (state) => ({
    user: state.userReducer
});

export default connect(mapStateToProps)(withRouter(PricingPage));